import {
  IonButton,
  IonCol,
  IonIcon,
  IonRow,
  IonSpinner,
  IonText,
} from '@ionic/react';
import ErrorBox from 'components/ErrorBox';
import { User } from 'firebase/auth';
import { logoApple, logoGoogle, mail } from 'ionicons/icons';
import _ from 'lodash';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthService } from 'services/Authentication';
import Trans from 'util/Trans';
import { AuthUser } from '../../models/User';
import MailSignInModal from './MailSignInModal';
import { logger } from 'services/Logger';

interface SignInButtonsProps {
  onLogIn?: (user: AuthUser, isNew: boolean) => void;
}

const SignInButtons: FC<SignInButtonsProps> = ({ onLogIn }) => {
  const { t } = useTranslation();
  const AuthService = useAuthService();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signIn = async (
    method: () => Promise<User>,
    isNewUser: () => boolean
  ) => {
    try {
      setIsLoading(true);
      const result = await method();

      if (result) {
        onLogIn?.(result, isNewUser());
        return null;
      } else {
        setError(t('COULD_NOT_SIGN_IN'));
        throw new Error('Retrieved User was null');
      }
    } catch (_error) {
      let msg = '';
      if (_error instanceof Error) msg = _error.message;
      else if (typeof _error === 'string') msg = _error;
      else msg = JSON.stringify(_error);

      if (msg.includes('There is no user record'))
        setError(t('INCORRECT_LOGIN_CREDENTIALS'));
      else setError(msg);
      /* setError(JSON.stringify(_error)); */
      logger.error("Error: " + _error);
      return msg;
    } finally {
      setIsLoading(false);
    }
  };

  const googleSignIn = async () => {
    await signIn(
      AuthService.signInWithGoogle.bind(AuthService),
      () => AuthService.isNewUser
    );
  };

  const appleSignIn = async () => {
    await signIn(
      AuthService.signInWithApple.bind(AuthService),
      () => AuthService.isNewUser
    );
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const mailSignIn = () => setIsOpen(true);
  const mailSignUp = () => setIsSignUpOpen(true);

  const onMailLogin = async (mail: string, password: string) => {
    return await signIn(
      _.partial(AuthService.signInWithMail.bind(AuthService), mail, password),
      () => AuthService.isNewUser
    );
  };
  const onMailSignUp = async (mail: string, password: string) => {
    return await signIn(
      _.partial(AuthService.signUpWithMail.bind(AuthService), mail, password),
      () => AuthService.isNewUser
    );
  };

  return (
    <>
      {process.env.NODE_ENV !== 'production' && (
        <>
          <MailSignInModal
            isOpen={isOpen}
            onLogin={onMailLogin}
            onDidDismiss={() => setIsOpen(false)}
          />
          <MailSignInModal
            isSignUp
            isOpen={isSignUpOpen}
            onLogin={onMailSignUp}
            onDidDismiss={() => setIsSignUpOpen(false)}
          />
        </>
      )}
      <IonRow>
      <IonCol className="errors ion-text-center error-message">
        <span>
        <IonSpinner name="crescent" className={`${isLoading ? '' : 'ion-hide'}`} />
        <ErrorBox error={error} />
        </span>
      </IonCol>
      </IonRow>
      <IonRow>
        <IonCol id="sign-in-buttons-col" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '400px',
          margin: '0 auto',
          width: '100%'
        }}>
          <div id="sign-in-buttons-horizontal">
            {process.env.NODE_ENV !== 'production' && (
              <>
                <IonButton
                  id="sign-in-button"
                  type="button"
                  expand="block"
                  onClick={mailSignIn}
                  disabled={isLoading}
                  style={{ width: '100%' }}
                >
                  <IonIcon color="secondary" icon={mail} slot="start" />
                  <IonText color="secondary">
                    <Trans i18nKey="MAIL_SIGNIN">Mit E-Mail einloggen</Trans>
                  </IonText>
                </IonButton>
                <IonButton
                  id="sign-up-button"
                  type="button"
                  expand="block"
                  onClick={mailSignUp}
                  disabled={isLoading}
                  style={{ width: '100%' }}
                >
                  <IonIcon color="secondary" icon={mail} slot="start" />
                  <IonText color="secondary">
                    <Trans i18nKey="MAIL_SIGNUP">Mit E-Mail anmelden</Trans>
                  </IonText>
                </IonButton>
              </>
            )}
          </div>
          <IonButton
            type="button"
            expand="block"
            onClick={googleSignIn}
            disabled={isLoading}
            style={{ width: '100%' }}
          >
            <IonIcon color="secondary" icon={logoGoogle} slot="start" />
            <IonText color="secondary">
              <Trans i18nKey="GOOGLE_SIGNIN">Mit Google einloggen</Trans>
            </IonText>
          </IonButton>
          <IonButton
            type="button"
            expand="block"
            onClick={appleSignIn}
            disabled={isLoading}
            style={{ width: '100%' }}
          >
            <IonIcon color="secondary" icon={logoApple} slot="start" />
            <IonText color="secondary">
              <Trans i18nKey="APPLE_SIGNIN">Mit Apple einloggen</Trans>
            </IonText>
          </IonButton>
        </IonCol>
      </IonRow>
    </>
  );
};
export default SignInButtons;
